// Minimal RFC 4180 CSV reader for the CRM's contact import.
//
// Deliberately hand-rolled rather than pulling in a parser: the input is a
// list export (Zoho, Mailchimp, a spreadsheet), the failure mode that matters
// is a quoted field containing a comma or a newline, and that's about it.

// Splits on commas outside quotes, honouring "" as an escaped quote and
// allowing newlines inside a quoted field.
export function parseCsv(text) {
  const rows = []
  let row = []
  let field = ''
  let inQuotes = false

  // Strip a UTF-8 BOM — Excel writes one and it otherwise becomes part of the
  // first header name, so "email" stops matching.
  const input = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n')

  for (let i = 0; i < input.length; i++) {
    const char = input[i]

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') { field += '"'; i++ }
        else inQuotes = false
      } else {
        field += char
      }
      continue
    }

    if (char === '"') inQuotes = true
    else if (char === ',') { row.push(field); field = '' }
    else if (char === '\n') { row.push(field); rows.push(row); row = []; field = '' }
    else field += char
  }

  // A file with no trailing newline still has a last row to flush.
  if (field !== '' || row.length) { row.push(field); rows.push(row) }

  return rows.filter((r) => r.some((cell) => cell.trim() !== ''))
}

// Header names vary by whoever exported the list; accept the common spellings
// rather than making the admin rename columns in a spreadsheet first.
const HEADER_ALIASES = {
  email: ['email', 'email address', 'e-mail', 'emailaddress', 'mail'],
  first_name: ['first name', 'firstname', 'first', 'given name', 'fname'],
  last_name: ['last name', 'lastname', 'last', 'surname', 'family name', 'lname'],
}

function matchHeader(name) {
  const clean = name.trim().toLowerCase()
  for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(clean)) return key
  }
  return null
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

// Turns raw CSV text into { rows, skipped, error }. A file with no recognisable
// email column is an error, not an empty import — silently importing nothing
// is the worst possible answer.
export function parseContactCsv(text) {
  const table = parseCsv(text)
  if (!table.length) return { rows: [], skipped: [], error: 'That file is empty.' }

  const header = table[0].map(matchHeader)
  const emailIndex = header.indexOf('email')

  // A single-column file of bare addresses is a perfectly normal export and
  // shouldn't need a header row.
  if (emailIndex === -1) {
    if (table[0].length === 1 && EMAIL_RE.test(table[0][0].trim())) {
      const rows = []
      const skipped = []
      for (const line of table) {
        const email = line[0].trim().toLowerCase()
        if (EMAIL_RE.test(email)) rows.push({ email })
        else skipped.push(line[0])
      }
      return dedupe(rows, skipped)
    }
    return {
      rows: [],
      skipped: [],
      error: 'No email column found. Add a header row with a column named "email".',
    }
  }

  const firstIndex = header.indexOf('first_name')
  const lastIndex = header.indexOf('last_name')

  const rows = []
  const skipped = []
  for (const line of table.slice(1)) {
    const email = (line[emailIndex] ?? '').trim().toLowerCase()
    if (!EMAIL_RE.test(email)) {
      if (email) skipped.push(email)
      continue
    }
    rows.push({
      email,
      first_name: firstIndex >= 0 ? (line[firstIndex] ?? '').trim() || null : null,
      last_name: lastIndex >= 0 ? (line[lastIndex] ?? '').trim() || null : null,
    })
  }

  return dedupe(rows, skipped)
}

// The database dedupes too, but doing it here means the "importing 412
// contacts" count is the truth rather than the row count of the file.
function dedupe(rows, skipped) {
  const seen = new Set()
  const unique = []
  for (const row of rows) {
    if (seen.has(row.email)) continue
    seen.add(row.email)
    unique.push(row)
  }
  return { rows: unique, skipped, error: null, duplicates: rows.length - unique.length }
}
